import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, FileText, Sparkles, ClipboardList, Map, Settings, Puzzle, MessageSquare, Camera, Calendar, CreditCard, Ghost, ShieldAlert, ClipboardCheck } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import LightSwitch from '../components/LightSwitch';
import CursorToggle from '../components/CursorToggle';
import ContentEditor from '../components/admin/ContentEditor';
import AutoPostGenerator from '../components/admin/AutoPostGenerator';
import TeamTasks from '../components/admin/TeamTasks';
import TreasureEditor from '../components/admin/TreasureEditor';
import SiteSettings from '../components/admin/SiteSettings';
import PuzzleManager from '../components/admin/PuzzleManager';
import SmsMessenger from '../components/admin/SmsMessenger';
import PhotoBrander from '../components/admin/PhotoBrander';
import GoogleCalendar from '../components/admin/GoogleCalendar';
import AdminReminders from '../components/admin/AdminReminders';
import SquareSettings from '../components/admin/SquareSettings';
import HauntedHouse from '../components/admin/HauntedHouse';
import WaiversAdmin from '../components/admin/WaiversAdmin';
import CheckInSearch from '../components/admin/CheckInSearch';

const TABS = [
  { id: 'checkin', label: 'Check-In', icon: ClipboardCheck },
  { id: 'haunted', label: 'Haunted House', icon: Ghost },
  { id: 'waivers', label: 'Waivers', icon: ShieldAlert },
  { id: 'team', label: 'Team Tasks & Daily Log', icon: ClipboardList },
  { id: 'sms', label: 'Message the Customer', icon: MessageSquare },
  { id: 'brand', label: 'Photo Branding', icon: Camera },
  { id: 'google', label: 'Google', icon: Calendar },
  { id: 'square', label: 'Square', icon: CreditCard },
  { id: 'puzzle', label: 'Weekly Puzzle', icon: Puzzle },
  { id: 'auto', label: 'Post Generator', icon: Sparkles },
  { id: 'treasure', label: 'Treasure Hunt', icon: Map },
  { id: 'content', label: 'Site Content', icon: FileText },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('team');

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <a href="/" className="font-heading text-lg tracking-wider">OKI-<span className="text-brand">ESCAPE</span></a>
            <span className="font-heading text-xs tracking-widest uppercase text-muted-foreground hidden sm:inline">Admin Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-sans text-xs text-muted-foreground hidden sm:inline">{user?.email}</span>
            <button onClick={handleLogout} data-testid="admin-logout-btn" className="flex items-center gap-1 font-heading text-xs tracking-widest uppercase text-muted-foreground hover:text-brand transition-colors px-3 py-2">
              <LogOut size={14} /><span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto pb-0">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 font-heading text-xs tracking-widest uppercase px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-brand text-brand' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="hidden sm:block fixed right-4 top-36 z-40">
        <LightSwitch />
        <CursorToggle />
      </div>
      {activeTab === 'content' && <ContentEditor />}
      {activeTab === 'auto' && <AutoPostGenerator />}
      {activeTab === 'team' && <TeamTasks />}
      {activeTab === 'sms' && <SmsMessenger />}
      {activeTab === 'brand' && <PhotoBrander />}
      {activeTab === 'google' && <GoogleCalendar />}
      {activeTab === 'square' && <SquareSettings />}
      {activeTab === 'puzzle' && <PuzzleManager />}
      {activeTab === 'treasure' && <TreasureEditor />}
      {activeTab === 'settings' && <SiteSettings />}
      {activeTab === 'haunted' && <HauntedHouse />}
      {activeTab === 'waivers' && <WaiversAdmin />}
      {activeTab === 'checkin' && <CheckInSearch />}
      <AdminReminders />
    </div>
  );
}
